// =====================================================
// PAGE ADMIN: LISTE PATIENTS PSY
// =====================================================

import { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Search, Filter, Eye, User, Calendar, Phone, Mail, MapPin, ArrowUpDown, CheckCircle, XCircle, Edit, Save, Plus, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/ui/use-toast';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';

type SortField = 'name' | 'email' | 'sessions' | 'lastSession' | 'created';
type SortDirection = 'asc' | 'desc';

interface Patient {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  gender: string | null;
  city: string | null;
  country: string | null;
  preferred_language: string | null;
  total_sessions: number;
  last_session_at: string | null;
  initial_triage_completed: boolean;
  triage_completed_at: string | null;
  consent_data_processing: boolean;
  consent_teletherapy: boolean;
  created_at: string;
  last_login_at: string | null;
}

export default function AdminPatientsList() {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [triageFilter, setTriageFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Patient>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    city: '',
    country: 'Cameroun',
    preferred_language: 'Français',
    consent_data_processing: false,
    consent_teletherapy: false,
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, triageFilter, patients, sortField, sortDirection]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const { domain, tenantId } = getDomainFilterParams();
      
      let query = supabase
        .from('psy_patients')
        .select('*');

      // Appliquer le filtrage par domaine
      query = applyDomainFilterToQuery(query, domain, tenantId);

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setPatients(data || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement des patients',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const applyFilters = () => {
    let filtered = [...patients];

    // Recherche
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre triage
    if (triageFilter !== 'all') {
      if (triageFilter === 'completed') {
        filtered = filtered.filter((p) => p.initial_triage_completed);
      } else if (triageFilter === 'pending') {
        filtered = filtered.filter((p) => !p.initial_triage_completed);
      }
    }

    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = (a.full_name || '').localeCompare(b.full_name || '');
          break;
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '');
          break;
        case 'sessions':
          comparison = (a.total_sessions || 0) - (b.total_sessions || 0);
          break;
        case 'lastSession':
          comparison = (a.last_session_at || '').localeCompare(b.last_session_at || '');
          break;
        case 'created':
          comparison = (a.created_at || '').localeCompare(b.created_at || '');
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredPatients(filtered);
  };

  const getTriageBadge = (completed: boolean) => {
    if (completed) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Complété</Badge>;
    }
    return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">En attente</Badge>;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateAge = (birthDate: string | null) => {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return `${age} ans`;
  };

  const handleViewDetails = (patient: Patient) => {
    setSelectedPatient(patient);
    setEditForm(patient);
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditForm(selectedPatient || {});
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedPatient) return;

    try {
      setIsSaving(true);
      const { domain, tenantId } = getDomainFilterParams();

      const { error } = await supabase
        .from('psy_patients')
        .update({
          full_name: editForm.full_name,
          email: editForm.email,
          phone: editForm.phone,
          date_of_birth: editForm.date_of_birth,
          gender: editForm.gender,
          city: editForm.city,
          country: editForm.country,
          preferred_language: editForm.preferred_language,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedPatient.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Les informations du patient ont été mises à jour',
      });

      // Recharger les patients
      await loadPatients();
      
      // Mettre à jour le patient sélectionné
      setSelectedPatient({ ...selectedPatient, ...editForm } as Patient);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreatePatient = async () => {
    try {
      setIsCreating(true);
      const { domain, tenantId } = getDomainFilterParams();

      const { data, error } = await supabase
        .from('psy_patients')
        .insert({
          full_name: createForm.full_name.trim(),
          email: createForm.email.trim(),
          phone: createForm.phone?.trim() || null,
          date_of_birth: createForm.date_of_birth || null,
          gender: createForm.gender || null,
          city: createForm.city?.trim() || null,
          country: createForm.country?.trim() || 'Cameroun',
          preferred_language: createForm.preferred_language || 'Français',
          consent_data_processing: createForm.consent_data_processing,
          consent_teletherapy: createForm.consent_teletherapy,
          consent_signed_at: createForm.consent_data_processing || createForm.consent_teletherapy ? new Date().toISOString() : null,
          initial_triage_completed: false,
          total_sessions: 0,
          domain: domain,
          tenant_id: tenantId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Succès',
        description: `Le patient ${createForm.full_name} a été créé avec succès`,
      });

      // Réinitialiser le formulaire
      setCreateForm({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        gender: '',
        city: '',
        country: 'Cameroun',
        preferred_language: 'Français',
        consent_data_processing: false,
        consent_teletherapy: false,
      });

      setShowCreateModal(false);
      
      // Recharger la liste des patients
      await loadPatients();
    } catch (error: any) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de la création du patient',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <User className="h-8 w-8 text-[#D4AF37]" />
              Gestion des Patients
            </h1>
            <p className="text-gray-400 mt-2">
              {filteredPatients.length} patient{filteredPatients.length > 1 ? 's' : ''} trouvé{filteredPatients.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
          >
            <Plus className="mr-2" size={18} />
            Nouveau patient
          </Button>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Rechercher par nom, email, téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>

            {/* Filtre Triage */}
            <div>
              <select
                value={triageFilter}
                onChange={(e) => setTriageFilter(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
              >
                <option value="all" className="bg-gray-900">Tous les patients</option>
                <option value="completed" className="bg-gray-900">Triage complété</option>
                <option value="pending" className="bg-gray-900">Triage en attente</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{patients.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Triage complété</div>
            <div className="text-2xl font-bold text-green-400">
              {patients.filter((p) => p.initial_triage_completed).length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Triage en attente</div>
            <div className="text-2xl font-bold text-yellow-400">
              {patients.filter((p) => !p.initial_triage_completed).length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Sessions totales</div>
            <div className="text-2xl font-bold text-[#D4AF37]">
              {patients.reduce((sum, p) => sum + (p.total_sessions || 0), 0)}
            </div>
          </div>
        </div>

        {/* Table des patients */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="text-center py-12">
              <User className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucun patient trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Patient
                        <ArrowUpDown size={14} className={sortField === 'name' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('email')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Contact
                        <ArrowUpDown size={14} className={sortField === 'email' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Localisation
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('sessions')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Sessions
                        <ArrowUpDown size={14} className={sortField === 'sessions' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Triage
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Inscription
                        <ArrowUpDown size={14} className={sortField === 'created' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredPatients.map((patient, index) => (
                    <tr
                      key={patient.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {patient.full_name || 'Non renseigné'}
                            </div>
                            <div className="text-sm text-gray-400">
                              {patient.gender || 'N/A'} • {calculateAge(patient.date_of_birth)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{patient.email || 'N/A'}</div>
                        {patient.phone && (
                          <div className="text-sm text-gray-400">{patient.phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">
                          {patient.city && patient.country
                            ? `${patient.city}, ${patient.country}`
                            : patient.city || patient.country || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{patient.total_sessions || 0}</div>
                        {patient.last_session_at && (
                          <div className="text-xs text-gray-400">
                            {formatDate(patient.last_session_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTriageBadge(patient.initial_triage_completed)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {formatDate(patient.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(patient)}
                          className="text-[#D4AF37] hover:text-[#B8941F]"
                        >
                          <Eye size={18} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de détails */}
      {showDetailsModal && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-lg border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <User className="h-6 w-6 text-[#D4AF37]" />
                  Détails du Patient
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations personnelles */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-[#D4AF37]" />
                  Informations Personnelles
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Nom complet</label>
                    {isEditing ? (
                      <Input
                        value={editForm.full_name || ''}
                        onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Nom complet"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPatient.full_name || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Date de naissance</label>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={editForm.date_of_birth || ''}
                        onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                      />
                    ) : (
                      <p className="text-white font-medium">
                        {selectedPatient.date_of_birth
                          ? `${formatDate(selectedPatient.date_of_birth)} (${calculateAge(selectedPatient.date_of_birth)})`
                          : 'N/A'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Genre</label>
                    {isEditing ? (
                      <select
                        value={editForm.gender || ''}
                        onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      >
                        <option value="" className="bg-gray-900">Sélectionner</option>
                        <option value="Homme" className="bg-gray-900">Homme</option>
                        <option value="Femme" className="bg-gray-900">Femme</option>
                        <option value="Non-binaire" className="bg-gray-900">Non-binaire</option>
                        <option value="Autre" className="bg-gray-900">Autre</option>
                        <option value="Préfère ne pas répondre" className="bg-gray-900">Préfère ne pas répondre</option>
                      </select>
                    ) : (
                      <p className="text-white font-medium">{selectedPatient.gender || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Langue préférée</label>
                    {isEditing ? (
                      <select
                        value={editForm.preferred_language || ''}
                        onChange={(e) => setEditForm({ ...editForm, preferred_language: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      >
                        <option value="" className="bg-gray-900">Sélectionner</option>
                        <option value="Français" className="bg-gray-900">Français</option>
                        <option value="Anglais" className="bg-gray-900">Anglais</option>
                        <option value="Espagnol" className="bg-gray-900">Espagnol</option>
                        <option value="Arabe" className="bg-gray-900">Arabe</option>
                        <option value="Allemand" className="bg-gray-900">Allemand</option>
                        <option value="Italien" className="bg-gray-900">Italien</option>
                        <option value="Portugais" className="bg-gray-900">Portugais</option>
                        <option value="Chinois" className="bg-gray-900">Chinois</option>
                        <option value="Russe" className="bg-gray-900">Russe</option>
                        <option value="Japonais" className="bg-gray-900">Japonais</option>
                        <option value="Swahili" className="bg-gray-900">Swahili</option>
                        <option value="Fulfulde" className="bg-gray-900">Fulfulde</option>
                        <option value="Ewondo" className="bg-gray-900">Ewondo</option>
                        <option value="Duala" className="bg-gray-900">Duala</option>
                        <option value="Bamiléké" className="bg-gray-900">Bamiléké</option>
                        <option value="Autre" className="bg-gray-900">Autre</option>
                      </select>
                    ) : (
                      <p className="text-white font-medium">{selectedPatient.preferred_language || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-[#D4AF37]" />
                  Contact
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Email</label>
                    {isEditing ? (
                      <Input
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="email@example.com"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPatient.email || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Téléphone</label>
                    {isEditing ? (
                      <Input
                        type="tel"
                        value={editForm.phone || ''}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="+237 6XX XXX XXX"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPatient.phone || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Ville</label>
                    {isEditing ? (
                      <Input
                        value={editForm.city || ''}
                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Ville"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPatient.city || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Pays</label>
                    {isEditing ? (
                      <Input
                        value={editForm.country || ''}
                        onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Pays"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPatient.country || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Consentements */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
                  Consentements
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-300">Traitement des données</span>
                    {selectedPatient.consent_data_processing ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Accepté
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <XCircle className="h-3 w-3 mr-1" />
                        Refusé
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-300">Téléconsultation</span>
                    {selectedPatient.consent_teletherapy ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Accepté
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <XCircle className="h-3 w-3 mr-1" />
                        Refusé
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Sessions */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#D4AF37]" />
                  Activité
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-3xl font-bold text-[#D4AF37]">
                      {selectedPatient.total_sessions || 0}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">Sessions totales</div>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-sm text-gray-400">Dernière session</div>
                    <div className="text-white font-medium mt-1">
                      {formatDate(selectedPatient.last_session_at)}
                    </div>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-sm text-gray-400">Inscription</div>
                    <div className="text-white font-medium mt-1">
                      {formatDate(selectedPatient.created_at)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Triage */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Filter className="h-5 w-5 text-[#D4AF37]" />
                  Triage Initial
                </h3>
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Statut du triage</span>
                    {getTriageBadge(selectedPatient.initial_triage_completed)}
                  </div>
                  {selectedPatient.triage_completed_at && (
                    <div className="mt-2 text-sm text-gray-400">
                      Complété le: {formatDateTime(selectedPatient.triage_completed_at)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex justify-between">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleCancelEdit}
                    variant="outline"
                    className="border-white/10 text-gray-300 hover:bg-white/5"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Enregistrer
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleEdit}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>
                  <Button
                    onClick={() => setShowDetailsModal(false)}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
                  >
                    Fermer
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-lg border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <User className="h-6 w-6 text-[#D4AF37]" />
                  Nouveau Patient
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations personnelles */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-[#D4AF37]" />
                  Informations Personnelles
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Nom complet *</label>
                    <Input
                      value={createForm.full_name}
                      onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Nom complet"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Date de naissance</label>
                    <Input
                      type="date"
                      value={createForm.date_of_birth}
                      onChange={(e) => setCreateForm({ ...createForm, date_of_birth: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Genre</label>
                    <select
                      value={createForm.gender}
                      onChange={(e) => setCreateForm({ ...createForm, gender: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    >
                      <option value="" className="bg-gray-900">Sélectionner</option>
                      <option value="Homme" className="bg-gray-900">Homme</option>
                      <option value="Femme" className="bg-gray-900">Femme</option>
                      <option value="Non-binaire" className="bg-gray-900">Non-binaire</option>
                      <option value="Autre" className="bg-gray-900">Autre</option>
                      <option value="Préfère ne pas répondre" className="bg-gray-900">Préfère ne pas répondre</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Langue préférée</label>
                    <select
                      value={createForm.preferred_language}
                      onChange={(e) => setCreateForm({ ...createForm, preferred_language: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    >
                      <option value="Français" className="bg-gray-900">Français</option>
                      <option value="Anglais" className="bg-gray-900">Anglais</option>
                      <option value="Espagnol" className="bg-gray-900">Espagnol</option>
                      <option value="Arabe" className="bg-gray-900">Arabe</option>
                      <option value="Allemand" className="bg-gray-900">Allemand</option>
                      <option value="Italien" className="bg-gray-900">Italien</option>
                      <option value="Portugais" className="bg-gray-900">Portugais</option>
                      <option value="Chinois" className="bg-gray-900">Chinois</option>
                      <option value="Russe" className="bg-gray-900">Russe</option>
                      <option value="Japonais" className="bg-gray-900">Japonais</option>
                      <option value="Swahili" className="bg-gray-900">Swahili</option>
                      <option value="Fulfulde" className="bg-gray-900">Fulfulde</option>
                      <option value="Ewondo" className="bg-gray-900">Ewondo</option>
                      <option value="Duala" className="bg-gray-900">Duala</option>
                      <option value="Bamiléké" className="bg-gray-900">Bamiléké</option>
                      <option value="Autre" className="bg-gray-900">Autre</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-[#D4AF37]" />
                  Contact
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Email *</label>
                    <Input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Téléphone</label>
                    <Input
                      type="tel"
                      value={createForm.phone}
                      onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="+237 6XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Ville</label>
                    <Input
                      value={createForm.city}
                      onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Ville"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Pays</label>
                    <Input
                      value={createForm.country}
                      onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Pays"
                    />
                  </div>
                </div>
              </div>

              {/* Consentements */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
                  Consentements
                </h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                    <input
                      type="checkbox"
                      checked={createForm.consent_data_processing}
                      onChange={(e) => setCreateForm({ ...createForm, consent_data_processing: e.target.checked })}
                      className="w-5 h-5 rounded border-white/10 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37] focus:ring-offset-0"
                    />
                    <span className="text-gray-300">Consentement au traitement des données</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                    <input
                      type="checkbox"
                      checked={createForm.consent_teletherapy}
                      onChange={(e) => setCreateForm({ ...createForm, consent_teletherapy: e.target.checked })}
                      className="w-5 h-5 rounded border-white/10 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37] focus:ring-offset-0"
                    />
                    <span className="text-gray-300">Consentement à la téléconsultation</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex justify-between">
              <Button
                onClick={() => setShowCreateModal(false)}
                variant="outline"
                className="border-white/10 text-gray-300 hover:bg-white/5"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreatePatient}
                disabled={isCreating || !createForm.full_name || !createForm.email}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Créer le patient
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
